import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sparklines, SparklinesLine, SparklinesSpots } from 'react-sparklines';
import { useCards } from '../../utils/reactQueryHooks';
import {
    checkDiscreteCard,
    generateMockDiscreteReadings,
    DISCRETE_UPDATE_CADENCE,
} from '../../utils/cardUtils';
import { processTimeStamp } from '../../utils/chartUtils';
import '../../styles/discreteSensor.css';

// Mocked detail experience for a discrete / LoRa sensor (e.g. outdoor weather
// station or in-ground soil-moisture probe). Until the backend ingests LoRa
// uplinks, everything here runs on client-side generated data so the full
// interaction can be reviewed.
export default function DiscreteSensorWrapper() {
    const { macAddress } = useParams();
    const navigate = useNavigate();
    const { data: cards = [] } = useCards();

    const sensorCard = useMemo(
        () => (macAddress ? cards.find((c) => c.macAddress === macAddress) || null : null),
        [cards, macAddress]
    );

    // 24 hours of mocked hourly readings for this device.
    const readings = useMemo(() => generateMockDiscreteReadings(macAddress, 24), [macAddress]);
    const latest = readings[readings.length - 1] || {};

    const soilSeries = readings.map((r) => r.soilMoisture);
    const tempSeries = readings.map((r) => r.temperature);

    // MOCK sprinkler aggregate: average soil moisture across every discrete
    // soil sensor on the account — the value a future "auto sprinkler" action
    // would key off of.
    const discreteSoilCards = useMemo(
        () => cards.filter((c) => checkDiscreteCard(c.macAddress)),
        [cards]
    );
    const fleetAverageMoisture = useMemo(() => {
        if (discreteSoilCards.length === 0) return null;
        const total = discreteSoilCards.reduce((sum, c) => {
            const latestForCard =
                generateMockDiscreteReadings(c.macAddress, 1)[0]?.soilMoisture ?? 0;
            return sum + latestForCard;
        }, 0);
        return Math.round((total / discreteSoilCards.length) * 10) / 10;
    }, [discreteSoilCards]);

    const handleBack = () => navigate(-1);

    return (
        <div className="discrete-detail">
            <div className="zone-header">
                <button className="zone-control-btn" onClick={handleBack}>
                    <i className="ri-arrow-left-line"></i>
                    <span>Back</span>
                </button>
                <div className="zone-header-main">
                    <div className="zone-title-row">
                        <h2 className="zone-title">
                            {sensorCard?.sensorDescription || 'Discrete Sensor'}
                        </h2>
                        <span className="discrete-badge">
                            <i className="ri-base-station-line"></i>
                            LoRa · {DISCRETE_UPDATE_CADENCE}
                        </span>
                    </div>
                    <p className="zone-updated">
                        Last reading: {processTimeStamp(latest.timestamp) || '--'}
                    </p>
                </div>
            </div>

            <div className="discrete-mock-banner">
                <i className="ri-flask-line"></i>
                <span>
                    Preview — this discrete device is mocked with sample hourly data. Live LoRa
                    uplink ingestion is not connected yet.
                </span>
            </div>

            <div className="discrete-vitals">
                <div className="discrete-vital">
                    <span className="discrete-vital-icon">🌱</span>
                    <span className="discrete-vital-value">
                        {latest.soilMoisture != null ? `${latest.soilMoisture}%` : '--'}
                    </span>
                    <span className="discrete-vital-label">Soil moisture</span>
                </div>
                <div className="discrete-vital">
                    <span className="discrete-vital-icon">🌡️</span>
                    <span className="discrete-vital-value">
                        {latest.temperature != null ? `${latest.temperature} °F` : '--'}
                    </span>
                    <span className="discrete-vital-label">Temperature</span>
                </div>
                <div className="discrete-vital">
                    <span className="discrete-vital-icon">🔋</span>
                    <span className="discrete-vital-value">
                        {latest.battery != null ? `${latest.battery} V` : '--'}
                    </span>
                    <span className="discrete-vital-label">Battery</span>
                </div>
                <div className="discrete-vital">
                    <span className="discrete-vital-icon">⏱️</span>
                    <span className="discrete-vital-value">{DISCRETE_UPDATE_CADENCE}</span>
                    <span className="discrete-vital-label">Update cadence</span>
                </div>
            </div>

            <div className="discrete-charts">
                <div className="discrete-chart-card">
                    <h3 className="discrete-chart-title">Soil moisture · last 24h</h3>
                    <Sparklines data={soilSeries} width={320} height={80} margin={4}>
                        <SparklinesLine color="#38bdf8" style={{ fillOpacity: 0.25 }} />
                        <SparklinesSpots size={2} style={{ fill: '#38bdf8' }} />
                    </Sparklines>
                </div>
                <div className="discrete-chart-card">
                    <h3 className="discrete-chart-title">Temperature · last 24h</h3>
                    <Sparklines data={tempSeries} width={320} height={80} margin={4}>
                        <SparklinesLine color="#ff9a3c" style={{ fillOpacity: 0.25 }} />
                        <SparklinesSpots size={2} style={{ fill: '#ff9a3c' }} />
                    </Sparklines>
                </div>
            </div>

            <div className="discrete-readings">
                <h3 className="discrete-section-title">Hourly readings</h3>
                <div className="discrete-readings-table">
                    <div className="discrete-readings-head">
                        <span>Time</span>
                        <span>Soil</span>
                        <span>Temp</span>
                        <span>Battery</span>
                    </div>
                    {[...readings].reverse().map((r) => (
                        <div className="discrete-readings-row" key={r.timestamp}>
                            <span>{processTimeStamp(r.timestamp)}</span>
                            <span>{r.soilMoisture}%</span>
                            <span>{r.temperature} °F</span>
                            <span>{r.battery} V</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="discrete-sprinkler">
                <div className="discrete-sprinkler-head">
                    <h3 className="discrete-section-title">Sprinkler control</h3>
                    <span className="discrete-soon-pill">Coming soon</span>
                </div>
                <p className="discrete-sprinkler-desc">
                    A future action will trigger sprinklers automatically when the average soil
                    moisture across all in-ground sensors drops below a threshold.
                </p>
                <div className="discrete-sprinkler-metric">
                    <span className="discrete-sprinkler-metric-label">
                        Fleet average soil moisture ({discreteSoilCards.length} sensor
                        {discreteSoilCards.length === 1 ? '' : 's'})
                    </span>
                    <span className="discrete-sprinkler-metric-value">
                        {fleetAverageMoisture != null ? `${fleetAverageMoisture}%` : '--'}
                    </span>
                </div>
                <button className="discrete-sprinkler-btn" type="button" disabled>
                    <i className="ri-showers-line"></i>
                    Run sprinklers
                </button>
            </div>
        </div>
    );
}
